<script setup>
import { ref, computed, nextTick, watch } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useToggle } from '@vueuse/core';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import MultiselectDropdownItems from 'shared/components/ui/MultiselectDropdownItems.vue';

const props = defineProps({
  attribute: {
    type: Object,
    required: true,
  },
  selectedValue: {
    type: [String, Number, Boolean, Array, Object],
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['apply-filter', 'clear-filter']);

const [showDropdown, toggleDropdown] = useToggle(false);
const buttonRef = ref(null);
const dropdownStyle = ref({});

// For text/number inputs
const textValue = ref('');
const numberValue = ref('');
const dateValue = ref('');

// For operators (number, date, text)
const operator = ref('contains'); // Default for text, will be changed to 'equals' for number/date

// For list/checkbox
const selectedListItems = ref([]);

// Operators for number/date types
const operators = [
  { value: 'equals', label: 'Igual a' },
  { value: 'not_equals', label: 'Diferente de' },
  { value: 'greater_than', label: 'Maior que' },
  { value: 'less_than', label: 'Menor que' },
  { value: 'greater_than_or_equal', label: 'Maior ou igual' },
  { value: 'less_than_or_equal', label: 'Menor ou igual' },
];

// Operators for text
const textOperators = [
  { value: 'contains', label: 'Contém' },
  { value: 'not_contains', label: 'Não contém' },
  { value: 'equals', label: 'Igual a' },
  { value: 'starts_with', label: 'Começa com' },
  { value: 'ends_with', label: 'Termina com' },
];

const attributeType = computed(() => {
  // Map enum values: text: 0, number: 1, currency: 2, percent: 3, link: 4, date: 5, list: 6, checkbox: 7
  const type = props.attribute.attributeDisplayType;
  if (typeof type === 'string') {
    return type;
  }
  const typeMap = {
    0: 'text',
    1: 'number',
    2: 'currency',
    3: 'percent',
    4: 'link',
    5: 'date',
    6: 'list',
    7: 'checkbox',
  };
  return typeMap[type] || 'text';
});

const isListType = computed(() => attributeType.value === 'list');
const isCheckboxType = computed(() => attributeType.value === 'checkbox');
const isNumberType = computed(() =>
  ['number', 'currency', 'percent'].includes(attributeType.value)
);
const isDateType = computed(() => attributeType.value === 'date');
const isTextType = computed(() =>
  ['text', 'link'].includes(attributeType.value)
);

const listOptions = computed(() => {
  if (!isListType.value) return [];
  return (props.attribute.attributeValues || []).map(value => ({
    id: value,
    name: value,
  }));
});

const hasFilter = computed(() => {
  if (isListType.value || isCheckboxType.value) {
    return selectedListItems.value.length > 0;
  }
  if (isTextType.value) {
    return textValue.value.trim().length > 0;
  }
  if (isNumberType.value) {
    return numberValue.value !== '' && numberValue.value !== null;
  }
  if (isDateType.value) {
    return dateValue.value !== '';
  }
  return false;
});

const displayText = computed(() => {
  if (hasFilter.value) {
    if (isListType.value || isCheckboxType.value) {
      return `${props.attribute.attributeDisplayName} (${selectedListItems.value.length})`;
    }
    if (isTextType.value) {
      return `${props.attribute.attributeDisplayName}: "${textValue.value}"`;
    }
    if (isNumberType.value) {
      const op = operators.find(o => o.value === operator.value);
      return `${props.attribute.attributeDisplayName} ${op?.label || ''} ${numberValue.value}`;
    }
    if (isDateType.value) {
      const op = operators.find(o => o.value === operator.value);
      return `${props.attribute.attributeDisplayName} ${op?.label || ''} ${dateValue.value}`;
    }
  }
  return props.attribute.attributeDisplayName;
});

const updateDropdownPosition = () => {
  if (buttonRef.value) {
    const rect = buttonRef.value.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Calculate initial position
    let top = rect.bottom + 4;
    let left = rect.left;
    const width = Math.max(rect.width, 320);

    // Check if dropdown would go off-screen to the right
    if (left + width > viewportWidth) {
      left = viewportWidth - width - 8; // 8px padding from edge
    }

    // Check if dropdown would go off-screen to the bottom
    // Estimate dropdown height (we'll adjust after render)
    const estimatedHeight = 300; // Approximate height for list dropdown
    if (top + estimatedHeight > viewportHeight) {
      // Position above the button instead
      top = rect.top - estimatedHeight - 4;
      // If still off-screen at top, position at top of viewport
      if (top < 0) {
        top = 8;
      }
    }

    // Ensure left is not negative
    if (left < 0) {
      left = 8;
    }

    dropdownStyle.value = {
      position: 'fixed', // Use fixed instead of absolute for teleported elements
      top: `${top}px`,
      left: `${left}px`,
      width: `${width}px`,
      zIndex: 9999,
    };
  }
};

watch(showDropdown, async isVisible => {
  if (isVisible) {
    // Wait for initial render
    await nextTick();
    updateDropdownPosition();

    // For list types, wait for MultiselectDropdownItems to fully render
    if (isListType.value) {
      // Multiple nextTick calls to ensure nested component is rendered
      await nextTick();
      await nextTick();
      // Use requestAnimationFrame for more reliable positioning after render
      requestAnimationFrame(() => {
        updateDropdownPosition();
      });
    }
  }
});

// Also watch listOptions to recalculate position when they change
watch(
  () => listOptions.value,
  () => {
    if (showDropdown.value && isListType.value) {
      nextTick(() => {
        requestAnimationFrame(() => {
          updateDropdownPosition();
        });
      });
    }
  },
  { deep: true }
);

const handleApplyFilter = () => {
  let filterValue = null;

  if (isListType.value || isCheckboxType.value) {
    filterValue = selectedListItems.value.map(item => item.id || item);
  } else if (isTextType.value) {
    filterValue = {
      operator: operator.value,
      value: textValue.value.trim(),
    };
  } else if (isNumberType.value) {
    filterValue = {
      operator: operator.value,
      value: parseFloat(numberValue.value),
    };
  } else if (isDateType.value) {
    filterValue = {
      operator: operator.value,
      value: dateValue.value,
    };
  }

  emit('apply-filter', {
    attributeKey: props.attribute.attributeKey,
    attributeId: props.attribute.id,
    value: filterValue,
  });

  toggleDropdown(false);
};

const handleClearFilter = () => {
  textValue.value = '';
  numberValue.value = '';
  dateValue.value = '';
  selectedListItems.value = [];
  operator.value = isTextType.value ? 'contains' : 'equals';

  emit('clear-filter', {
    attributeKey: props.attribute.attributeKey,
  });

  toggleDropdown(false);
};

const handleListSelect = item => {
  const index = selectedListItems.value.findIndex(
    selected => (selected.id || selected) === (item.id || item)
  );

  if (index > -1) {
    selectedListItems.value.splice(index, 1);
  } else {
    selectedListItems.value.push(item);
  }
};

// Set initial operator based on type
watch(
  () => attributeType.value,
  type => {
    if (type === 'text' || type === 'link') {
      operator.value = 'contains';
    } else {
      operator.value = 'equals';
    }
  },
  { immediate: true }
);

// Initialize from selectedValue prop
watch(
  () => props.selectedValue,
  newValue => {
    if (newValue) {
      if (isListType.value || isCheckboxType.value) {
        selectedListItems.value = Array.isArray(newValue)
          ? newValue.map(v => ({ id: String(v), name: String(v) }))
          : [{ id: String(newValue), name: String(newValue) }];
      } else if (
        typeof newValue === 'object' &&
        newValue.operator &&
        newValue.value !== undefined
      ) {
        operator.value = newValue.operator;
        if (isTextType.value) {
          textValue.value = newValue.value;
        } else if (isNumberType.value) {
          numberValue.value = newValue.value;
        } else if (isDateType.value) {
          dateValue.value = newValue.value;
        }
      }
    } else {
      // Reset values when filter is cleared
      textValue.value = '';
      numberValue.value = '';
      dateValue.value = '';
      selectedListItems.value = [];
    }
  },
  { immediate: true }
);
</script>

<template>
  <div ref="buttonRef" class="relative">
    <Button
      slate
      outline
      trailing-icon
      :icon="showDropdown ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
      class="w-full !px-3 !py-2 min-h-[36px]"
      :disabled="disabled"
      @click="toggleDropdown()"
    >
      <div class="flex items-center justify-between w-full min-w-0">
        <div class="flex items-center space-x-2 min-w-0">
          <h4 class="text-sm text-ellipsis text-n-slate-12 truncate">
            {{ displayText }}
          </h4>
          <span
            v-if="hasFilter"
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400 flex-shrink-0"
          >
            ✓
          </span>
        </div>
      </div>
    </Button>

    <teleport to="body">
      <OnClickOutside @trigger="toggleDropdown(false)">
        <div
          v-if="showDropdown"
          :style="dropdownStyle"
          class="box-border border rounded-lg bg-n-alpha-3 backdrop-blur-[100px] absolute shadow-lg border-n-strong dark:border-n-strong p-4 z-[9999]"
        >
          <div class="flex items-center justify-between mb-3">
            <h4 class="m-0 text-sm font-medium text-n-slate-12">
              Filtrar: {{ attribute.attributeDisplayName }}
            </h4>
            <Button
              ghost
              slate
              xs
              icon="i-lucide-x"
              @click="toggleDropdown(false)"
            />
          </div>

          <!-- List Type -->
          <div v-if="isListType" class="space-y-3">
            <MultiselectDropdownItems
              :options="listOptions"
              :selected-items="selectedListItems"
              :has-thumbnail="false"
              :input-placeholder="`Buscar ${attribute.attributeDisplayName.toLowerCase()}...`"
              no-search-result="Nenhum resultado encontrado"
              @select="handleListSelect"
            />
          </div>

          <!-- Checkbox Type -->
          <div v-else-if="isCheckboxType" class="space-y-3">
            <div class="space-y-2">
              <label class="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  :name="`checkbox-filter-${attribute.id}`"
                  :checked="
                    selectedListItems.length > 0 &&
                    selectedListItems[0]?.id === 'true'
                  "
                  class="w-4 h-4 text-blue-600 border-n-weak focus:ring-blue-500"
                  @change="selectedListItems = [{ id: 'true', name: 'Sim' }]"
                />
                <span class="text-sm text-n-slate-12">Sim</span>
              </label>
              <label class="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  :name="`checkbox-filter-${attribute.id}`"
                  :checked="
                    selectedListItems.length > 0 &&
                    selectedListItems[0]?.id === 'false'
                  "
                  class="w-4 h-4 text-blue-600 border-n-weak focus:ring-blue-500"
                  @change="selectedListItems = [{ id: 'false', name: 'Não' }]"
                />
                <span class="text-sm text-n-slate-12">Não</span>
              </label>
            </div>
            <Button
              v-if="selectedListItems.length > 0"
              variant="ghost"
              color="slate"
              size="sm"
              class="mt-2"
              @click="selectedListItems = []"
            >
              Limpar seleção
            </Button>
          </div>

          <!-- Text/Link Type -->
          <div v-else-if="isTextType" class="space-y-3">
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                Operador
              </label>
              <select
                v-model="operator"
                class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-solid-3 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option
                  v-for="op in textOperators"
                  :key="op.value"
                  :value="op.value"
                >
                  {{ op.label }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                Valor
              </label>
              <Input
                v-model="textValue"
                :placeholder="`Digite o valor para ${attribute.attributeDisplayName}`"
                :type="attributeType === 'link' ? 'url' : 'text'"
              />
            </div>
          </div>

          <!-- Number/Currency/Percent Type -->
          <div v-else-if="isNumberType" class="space-y-3">
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                Operador
              </label>
              <select
                v-model="operator"
                class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-solid-3 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option
                  v-for="op in operators"
                  :key="op.value"
                  :value="op.value"
                >
                  {{ op.label }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                Valor
              </label>
              <Input
                v-model="numberValue"
                type="number"
                :placeholder="`Digite o valor para ${attribute.attributeDisplayName}`"
              />
            </div>
          </div>

          <!-- Date Type -->
          <div v-else-if="isDateType" class="space-y-3">
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                Operador
              </label>
              <select
                v-model="operator"
                class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-solid-3 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option
                  v-for="op in operators"
                  :key="op.value"
                  :value="op.value"
                >
                  {{ op.label }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                Data
              </label>
              <Input
                v-model="dateValue"
                type="date"
                :placeholder="`Selecione a data para ${attribute.attributeDisplayName}`"
              />
            </div>
          </div>

          <!-- Actions -->
          <div
            class="flex items-center justify-between mt-4 pt-3 border-t border-n-weak"
          >
            <div class="flex items-center space-x-2 ml-auto">
              <Button
                variant="outline"
                color="slate"
                size="sm"
                @click="toggleDropdown(false)"
              >
                Cancelar
              </Button>
              <Button
                color="blue"
                size="sm"
                :disabled="!hasFilter"
                @click="handleApplyFilter"
              >
                Aplicar
              </Button>
            </div>
          </div>
        </div>
      </OnClickOutside>
    </teleport>
  </div>
</template>
